import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDisplayMode } from "../context/DisplayModeContext";
import { useGuardedNavigate } from "../../contexts/NavigationGuardContext";
import { useBreadcrumbStackOptional } from "../contexts/BreadcrumbStackContext";
import { isRewampKey, useRewampT } from "../localisation";
import { ReactComponent as BackBreadcrumbIcon } from "../../assets/rewamp/navigation/back-breadcrumb.svg";

// Segment → i18n key; conditional ones (Home, standalone "history") live in getLabel().
const SEGMENT_LABEL_KEYS = {
  schemes: "schemes.governmentSchemes",
  details: "schemes.breadcrumbSchemeDetails",
  apply: "schemes.breadcrumbApplyScheme",
  "grm-create": "agentGrm.GRM",
  "grm-application": "agentGrm.GRM_APPLICATION",
  "grievance-redressal": "agentGrm.GRM",
  "farmer-passbook": "FARMER_PASSBOOK",
  "soil-card": "soil_health_card",
  "soil-lab": "soil_health_labs",
  registration: "AGENT_FARMER_REGISTRATIONS",
  faq: "DFSWEB_FAQs",
  "key-contacts": "KeyContacts",
  "about-section": "DFSWEB_About_Bihar",
  "assets-section": "DFSWEB_ASSETS",
  "assets-details": "DFSWEB_ASSETS_details",
  horti: "HORTI_MODULE_TITLE",
  "provider-details": "Details",
  "enquiry-history": "HORTI_ENQUIRY_HISTORY",
  "application-details": "Details",
  applied: "Applied_Schemes",
  "farmer-registration": "Farmer_Registration",
  "recover-dbt-id": "Recover_DBT_ID",
  dashboards: "DFSWEB_DASHBOARD",
  "accessibility-statement": "AccessibilityStatement",
  login: "DFSWEB_LOGIN_PAGE",
  // Same key as the landing page's Bazar tab; the page's extraCrumbLabel adds the next crumb.
  "agri-market": "Bazaar",
  advisories: "ADVISORIES_PAGE.BREADCRUMB",
  help: "Help & Support",
  grievances: "GRM_LIST.BREADCRUMB",
  "tech-support": "TECH_LIST.BREADCRUMB",
};

// Trail type matches the Back pill's 14px so the whole bar reads at one size.
const CRUMB_TEXT_SX = { fontSize: "14px", lineHeight: "normal" };
// Ancestors ellipsize only when one alone outgrows the line (full text in `title`);
// the current crumb wraps instead, since it is the one label that must stay readable.
const LINK_CRUMB_SX = {
  ...CRUMB_TEXT_SX,
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const CURRENT_CRUMB_SX = { ...CRUMB_TEXT_SX, overflowWrap: "anywhere" };

const RewampBreadCrumbsBar = (props) => {
  const { showBack = true, onBack, sx, extraCrumbLabel, hideSegments = [], useStackTrail, title } = props;
  // Entries: string (current, non-clickable) or { label, to, state? } (link).
  const extraCrumbLabels = (extraCrumbLabel == null
    ? []
    : Array.isArray(extraCrumbLabel)
      ? extraCrumbLabel
      : [extraCrumbLabel]
  ).map((entry) =>
    typeof entry === "string" || entry == null
      ? { label: entry, to: null }
      : { label: entry.label, to: entry.to || null, state: entry.state }
  );
  const location = useLocation();
  const navigate = useGuardedNavigate();
  const stack = useBreadcrumbStackOptional();
  const { standalone } = useDisplayMode();
  const stackEntries = stack?.entries ?? [];
  const renderFromStack = useStackTrail !== false && stackEntries.length > 0;
  const { t } = useTranslation();
  const tRewamp = useRewampT();
  const theme = useTheme();

  // primary.interactive already carries the light/dark flip; current crumb is that colour @50%.
  const linkColor = theme.palette.primary.interactive;
  const currentColor = alpha(linkColor, 0.5);

  // ── Path → crumb segments (legacy normalisation, unchanged) ───────────────
  let pathnames = location.pathname.split("/").filter((x) => x);
  if (["dashboards"].some((path) => pathnames?.includes(path))) {
    pathnames.shift();
  }
  if (!window?.contextPath) {
    pathnames = ["home", ...pathnames];
  }
  if (props?.trackScheme && props?.trackScheme?.length === 1) {
    pathnames = [...pathnames, ...props.trackScheme];
  }
  if (["complaintdetails", "inbox"].some((path) => pathnames?.includes(path))) {
    pathnames.splice(-1);
  }

  // Router state carried when navigating to/through the scheme "details" crumb.
  const detailsState = {
    schemeId: props.schemeId,
    level: props.level,
    startDate: props.startDate,
    endDate: props.endDate,
    active: props.active,
    mainId: props.mainSchemeId,
  };

  const getLabel = (segment, index) => {
    if (index === 0) return t("DFSWEB_HOME");
    if (standalone && segment === "history") return t("DFSWEB_DASHBOARD");
    const key = SEGMENT_LABEL_KEYS[segment];
    if (key) return isRewampKey(key) ? tRewamp(key) : t(key);
    return t(segment.charAt(0).toUpperCase() + segment.slice(1));
  };

  // Cumulative route for the crumb at `index` (legacy route-building, unchanged).
  const buildRoute = (index) => {
    let startIndex = 0;
    if (!window.contextPath) {
      startIndex = pathnames?.length > 2 ? 1 : 0;
    }
    let routeTo = `/${pathnames.slice(startIndex, index + 1).join("/")}`;
    if (routeTo === "/grm") {
      routeTo = "/grm/inbox";
    }
    // Returns to the landing tab the assets page was opened from (e.g. Bazaar), else the Assets tab.
    const assetsHash = `#${location.state?.fromTab || "assets"}`;
    if (pathnames[index] === "assets-section") {
      return `${window.contextPath || ""}/${assetsHash}`;
    }
    const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
    if (routeTo === `${window.contextPath || ""}/home` && !isLoggedIn) {
      routeTo = `${window.contextPath || "/"}`;
    }
    if (
      isLoggedIn &&
      (routeTo === `${window.contextPath || ""}/home` || routeTo === "/home") &&
      (pathnames.includes("assets-section") || pathnames.includes("assets-details"))
    ) {
      return `${window.contextPath || ""}/home${assetsHash}`;
    }
    return routeTo;
  };

  const handleNavigation = (routeTo, state, hash) => {
    const isTrackSchemePage = pathnames.includes("trackScheme");
    const navOptions = { state, ...(hash ? { hash } : {}) };
    if (isTrackSchemePage) {
      props.handleService?.({});
    } else {
      navigate(routeTo, navOptions);
    }
  };

  const handleStackCrumbClick = (index) => {
    const entry = stackEntries[index];
    if (!entry) return;
    if (index === stackEntries.length - 1) return;
    // No pop here: the guarded navigate may be cancelled; the stack trims itself on location change.
    handleNavigation(entry.route, entry.state, entry.hash);
  };

  // "Back" = up one level in the trail (reuses the same guards as the crumbs),
  // unless the caller supplies an explicit onBack handler.
  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (renderFromStack) {
      const parent = stack?.getParent?.();
      if (parent) {
        handleNavigation(parent.route, parent.state, parent.hash);
        return;
      }
    }
    const parentIndex = pathnames.length - 2;
    if (parentIndex < 0) {
      navigate("/");
      return;
    }
    const parentSegment = pathnames[parentIndex];
    const state = parentSegment === "details" ? detailsState : {};
    handleNavigation(buildRoute(parentIndex), state);
  };

  if (standalone) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", ...sx }}>
        {showBack && (
          <IconButton
            size="touch"
            edge="start"
            aria-label={t("COMMON_BACK")}
            onClick={handleBack}
            sx={{ my: -1.25, mr: -1 }}
          >
            <BackBreadcrumbIcon aria-hidden="true" />
          </IconButton>
        )}
        {title && (
          <Typography component="h1" variant="callCentreTitle" sx={{ color: "neutral.headings" }}>
            {title}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "16px",
        width: "100%",
        ...sx,
      }}
    >
      {showBack && (
        <Button
          onClick={handleBack}
          disableElevation
          variant="navBack"
          startIcon={<KeyboardBackspaceIcon />}
        >
          {t("COMMON_BACK")}
        </Button>
      )}
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={
          <Box
            component="span"
            sx={{
              color: "text.textGrey",
              fontSize: "14px",
              lineHeight: "normal",
            }}
          >
            ›
          </Box>
        }
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          "& .MuiBreadcrumbs-separator": { mx: "6px" },
          "& .MuiBreadcrumbs-ol": { alignItems: "center" },
          "& .MuiBreadcrumbs-li": { minWidth: 0, maxWidth: "100%" },
        }}
      >
        {renderFromStack
          ? stackEntries.map((entry, index) => {
              const isLast = index === stackEntries.length - 1 && extraCrumbLabels.length === 0;
              const label = entry.label ?? (isRewampKey(entry.labelKey) ? tRewamp(entry.labelKey) : t(entry.labelKey));
              return isLast ? (
                <Typography
                  key={`stack-${entry.id}-${index}`}
                  aria-current="page"
                  sx={{ ...CURRENT_CRUMB_SX, color: currentColor, fontWeight: 400 }}
                >
                  {label}
                </Typography>
              ) : (
                <Link
                  key={`stack-${entry.id}-${index}`}
                  component={RouterLink}
                  to={entry.route}
                  underline="hover"
                  title={label}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStackCrumbClick(index);
                  }}
                  sx={{
                    ...LINK_CRUMB_SX,
                    color: linkColor,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </Link>
              );
            })
          : pathnames.map((segment, index) => {
          if (hideSegments.includes(segment)) return null;
          // With extraCrumbLabel the last URL segment becomes a link and the extra crumb is current.
          const isLast = index === pathnames.length - 1 && extraCrumbLabels.length === 0;
          const routeTo = buildRoute(index);
          const state = segment === "details" ? detailsState : {};
          const label = getLabel(segment, index);
          return isLast ? (
            <Typography
              key={`${segment}-${index}`}
              aria-current="page"
              sx={{ ...CURRENT_CRUMB_SX, color: currentColor, fontWeight: 400 }}
            >
              {label}
            </Typography>
          ) : (
            <Link
              key={`${segment}-${index}`}
              component={RouterLink}
              to={routeTo}
              underline="hover"
              title={label}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(routeTo, state);
              }}
              sx={{
                ...LINK_CRUMB_SX,
                color: linkColor,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {label}
            </Link>
          );
        })}
        {extraCrumbLabels.map((crumb, index) => {
          const isCurrent = index === extraCrumbLabels.length - 1 || !crumb.to;
          if (isCurrent) {
            return (
              <Typography
                key={`extra-${index}`}
                aria-current="page"
                sx={{ ...CURRENT_CRUMB_SX, color: currentColor, fontWeight: 400 }}
              >
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={`extra-${index}`}
              component={RouterLink}
              to={crumb.to}
              underline="hover"
              title={crumb.label}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(crumb.to, crumb.state || {});
              }}
              sx={{
                ...LINK_CRUMB_SX,
                color: linkColor,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

RewampBreadCrumbsBar.propTypes = {
  /** When true, appended to the crumb trail (legacy scheme-tracking case). */
  trackScheme: PropTypes.arrayOf(PropTypes.string),
  /** Reset handler invoked instead of navigating while on a trackScheme page. */
  handleService: PropTypes.func,
  /** Scheme-details router state (folded in for the "details" crumb). */
  schemeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  active: PropTypes.bool,
  mainSchemeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Show the green Back pill (default true). */
  showBack: PropTypes.bool,
  /** Override the default "up one level" Back behaviour. */
  onBack: PropTypes.func,
  /** Extra sx merged onto the outer row — the page owns vertical spacing. */
  sx: PropTypes.object,
  /** Trailing localized crumbs; `{ label, to }` links unless last. */
  extraCrumbLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ label: PropTypes.string, to: PropTypes.string }),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ label: PropTypes.string, to: PropTypes.string }),
      ])
    ),
  ]),
  /** URL segments hidden from the trail; routing and Back still see them. */
  hideSegments: PropTypes.arrayOf(PropTypes.string),
  /** When false, always use URL-derived crumbs even if a stack exists. */
  useStackTrail: PropTypes.bool,
  title: PropTypes.node,
};

export default RewampBreadCrumbsBar;
